import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Search,
  PlayArrow,
  CheckCircle,
  ArrowForward,
  MenuBook,
  Inventory2,
  PointOfSale,
  QrCodeScanner,
  Warehouse,
  BarChart,
  People,
  Storefront,
  Smartphone,
  NotificationsActive,
  BookmarkBorder,
  Bookmark,
  AccessTime,
  School,
  TrendingUp,
  Close,
  Tune,
  Star,
} from "@mui/icons-material";
import "./publicLearning.css";

const learningTopics = [
  {
    id: "getting-started",
    number: "01",
    title: "Getting Started",
    description:
      "Learn how to set up your business, add staff, and configure your inventory system.",
    icon: School,
    category: "Beginner",
    lessons: 8,
    duration: "18 min",
    progress: 100,
    featured: true,
  },
  {
    id: "inventory-basics",
    number: "02",
    title: "Inventory Basics",
    description:
      "Understand how to add products, categories, stock levels, and suppliers.",
    icon: Inventory2,
    category: "Beginner",
    lessons: 12,
    duration: "32 min",
    progress: 65,
  },
  {
    id: "pos-system-guide",
    number: "03",
    title: "POS System Guide",
    description:
      "Learn how to sell products, generate receipts, and manage daily transactions.",
    icon: PointOfSale,
    category: "Essential",
    lessons: 10,
    duration: "27 min",
    progress: 40,
  },
  {
    id: "mobile-scanning",
    number: "04",
    title: "Mobile Scanning",
    description:
      "Use your phone camera to scan barcodes instead of physical scanners.",
    icon: QrCodeScanner,
    category: "Essential",
    lessons: 6,
    duration: "14 min",
    progress: 0,
  },
  {
    id: "stock-management",
    number: "05",
    title: "Stock Management",
    description:
      "Track stock in real time, manage low stock alerts, and avoid shortages.",
    icon: Warehouse,
    category: "Advanced",
    lessons: 14,
    duration: "38 min",
    progress: 25,
  },
  {
    id: "sales-reports",
    number: "06",
    title: "Sales & Reports",
    description:
      "Analyze sales, profit, and performance using reports and dashboards.",
    icon: BarChart,
    category: "Advanced",
    lessons: 11,
    duration: "31 min",
    progress: 0,
  },
  {
    id: "staff-management",
    number: "07",
    title: "Staff Management",
    description:
      "Assign roles, control permissions, and monitor staff activities.",
    icon: People,
    category: "Advanced",
    lessons: 9,
    duration: "24 min",
    progress: 0,
  },
  {
    id: "multi-branch",
    number: "08",
    title: "Multi-Branch System",
    description:
      "Learn how to manage multiple stores and warehouses in one system.",
    icon: Storefront,
    category: "Advanced",
    lessons: 13,
    duration: "35 min",
    progress: 0,
  },
  {
    id: "mobile-app",
    number: "09",
    title: "Mobile App Usage",
    description:
      "Learn how to manage your business fully from your mobile app.",
    icon: Smartphone,
    category: "Mobile",
    lessons: 8,
    duration: "21 min",
    progress: 0,
  },
  {
    id: "real-time-notifications",
    number: "10",
    title: "Real-Time Notifications",
    description:
      "Understand how alerts notify you instantly when sales or stock changes happen.",
    icon: NotificationsActive,
    category: "Essential",
    lessons: 7,
    duration: "16 min",
    progress: 0,
  },
];

const categories = [
  "All",
  "Beginner",
  "Essential",
  "Advanced",
  "Mobile",
];

function PublicLearning() {
  const { topic } = useParams();
  const matchingTopic = learningTopics.find((item) =>
    item.id === ({
      "getting_started": "getting-started",
      "inventory_basics": "inventory-basics",
      "pos_system": "pos-system-guide",
      "mobile_scanning": "mobile-scanning",
      "stock_management": "stock-management",
      "sales_reports": "sales-reports",
      "staff_management": "staff-management",
      "multi_branch": "multi-branch",
      "mobile_app": "mobile-app",
      "notifications": "real-time-notifications",
    }[topic] || topic)
  );
  const [search, setSearch] = useState(() => matchingTopic?.title || "");
  useEffect(() => {
    setSearch(matchingTopic?.title || "");
  }, [topic]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [bookmarked, setBookmarked] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const filteredTopics = useMemo(() => {
    const query = search.trim().toLowerCase();

    return learningTopics.filter((topic) => {
      const matchesCategory =
        activeCategory === "All" ||
        topic.category === activeCategory;

      const matchesSearch =
        !query ||
        topic.title.toLowerCase().includes(query) ||
        topic.description.toLowerCase().includes(query) ||
        topic.category.toLowerCase().includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [search, activeCategory]);

  const completedTopics = learningTopics.filter(
    (topic) => topic.progress === 100
  ).length;

  const totalProgress = Math.round(
    learningTopics.reduce(
      (total, topic) => total + topic.progress,
      0
    ) / learningTopics.length
  );

  const toggleBookmark = (id) => {
    setBookmarked((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };

  const handleTopicClick = (topic) => {
    /*
     * Replace this with React Router navigation when your
     * individual learning routes are available.
     *
     * Example:
     *
     * navigate(`/learning/${topic.id}`);
     */
    console.log(`Opening learning topic: ${topic.id}`);
  };

  return (
    <main className="public_learning-page">
      <div className="public_learning-page__container">
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="public_learning-hero">
          <div className="public_learning-hero__content">
            <div className="public_learning-hero__eyebrow fx fx-ac g1">
              <MenuBook aria-hidden="true" />
              <span>UNIVERSE INVENTORY LEARNING CENTER</span>
            </div>

            <h1 className="public_learning-hero__title">
              Learn Universe Inventory.
              <br />
              <span>Run your business better.</span>
            </h1>

            <p className="public_learning-hero__description">
              Step-by-step guides to help you master inventory,
              POS, sales, staff, branches, mobile tools, and
              everything Universe Inventory has to offer.
            </p>

            {/* Search */}

            <div className="public_learning-search fx fx-ac g2">
              <Search aria-hidden="true" />

              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="What do you want to learn?"
                aria-label="Search learning resources"
              />

              {search && (
                <button
                  type="button"
                  className="public_learning-search__clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <Close aria-hidden="true" />
                </button>
              )}

              <button
                type="button"
                className="public_learning-search__button"
                onClick={() => {
                  document
                    .getElementById("public_learning-topics")
                    ?.scrollIntoView({
                      behavior: "smooth",
                    });
                }}
              >
                Search
              </button>
            </div>

            <div className="public_learning-hero__stats fx wrap g3">
              <div className="public_learning-hero-stat fx fx-ac g2">
                <div className="public_learning-hero-stat__icon">
                  <MenuBook aria-hidden="true" />
                </div>

                <div>
                  <strong>10</strong>
                  <span>Learning paths</span>
                </div>
              </div>

              <div className="public_learning-hero-stat fx fx-ac g2">
                <div className="public_learning-hero-stat__icon">
                  <TrendingUp aria-hidden="true" />
                </div>

                <div>
                  <strong>{totalProgress}%</strong>
                  <span>Your progress</span>
                </div>
              </div>

              <div className="public_learning-hero-stat fx fx-ac g2">
                <div className="public_learning-hero-stat__icon">
                  <CheckCircle aria-hidden="true" />
                </div>

                <div>
                  <strong>{completedTopics}</strong>
                  <span>Completed</span>
                </div>
              </div>
            </div>
          </div>

          <div className="public_learning-hero__visual">
            <div className="public_learning-hero__orb learning-hero__orb--one" />
            <div className="public_learning-hero__orb learning-hero__orb--two" />

            <div className="public_learning-hero-dashboard">
              <div className="public_learning-hero-dashboard__top fx fx-ac fx-jb">
                <div className="fx fx-ac g1">
                  <div className="public_learning-dashboard-dot" />
                  <span>Learning progress</span>
                </div>

                <TrendingUp aria-hidden="true" />
              </div>

              <div className="public_learning-progress-circle">
                <div>
                  <strong>{totalProgress}%</strong>
                  <span>Complete</span>
                </div>
              </div>

              <div className="public_learning-hero-dashboard__line">
                <span style={{ width: `${totalProgress}%` }} />
              </div>

              <div className="public_learning-hero-dashboard__footer fx fx-jb">
                <span>Learning journey</span>
                <strong>{completedTopics}/10 paths</strong>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            CONTINUE LEARNING
        ===================================================== */}

        <section className="public_learning-continue">
          <div className="public_learning-section-heading fx fx-jb fx-ac wrap g2">
            <div>
              <div className="public_learning-section-label">
                CONTINUE LEARNING
              </div>

              <h2>Pick up where you left off</h2>

              <p>
                Continue your learning journey and get more from
                Universe Inventory.
              </p>
            </div>

            <button
              type="button"
              className="public_learning-text-button"
              onClick={() => {
                setActiveCategory("All");
                setSearch("");
                document
                  .getElementById("public_learning-topics")
                  ?.scrollIntoView({
                    behavior: "smooth",
                  });
              }}
            >
              View all
              <ArrowForward aria-hidden="true" />
            </button>
          </div>

          <div className="public_learning-continue-card">
            <div className="public_learning-continue-card__icon">
              <Inventory2 aria-hidden="true" />
            </div>

            <div className="public_learning-continue-card__content">
              <div className="public_learning-card-meta">
                <span>IN PROGRESS</span>
                <span>Inventory Basics</span>
              </div>

              <h3>Inventory Basics</h3>

              <p>
                Understand products, categories, stock levels,
                suppliers, and the foundation of your inventory
                system.
              </p>

              <div className="public_learning-progress">
                <div className="public_learning-progress__top fx fx-jb">
                  <span>65% complete</span>
                  <span>8 of 12 lessons</span>
                </div>

                <div className="public_learning-progress__bar">
                  <span style={{ width: "65%" }} />
                </div>
              </div>

              <button
                type="button"
                className="public_learning-primary-button fx fx-ac fx-jc g1"
                onClick={() =>
                  handleTopicClick(
                    learningTopics.find(
                      (topic) => topic.id === "inventory-basics"
                    )
                  )
                }
              >
                <PlayArrow aria-hidden="true" />
                Continue Learning
              </button>
            </div>

            <div className="public_learning-continue-card__lesson">
              <span>Next lesson</span>
              <strong>Understanding Stock Levels</strong>
              <small>
                <AccessTime aria-hidden="true" />
                5 min
              </small>
            </div>
          </div>
        </section>

        {/* =====================================================
            LEARNING JOURNEY
        ===================================================== */}

        <section className="public_learning-journey">
          <div className="public_learning-section-heading">
            <div className="public_learning-section-label">
              YOUR LEARNING JOURNEY
            </div>

            <h2>Master Universe Inventory step by step</h2>

            <p>
              Start with the fundamentals and progress toward
              advanced business-management features.
            </p>
          </div>

          <div className="public_learning-journey__track">
            {learningTopics.slice(0, 5).map((topic, index) => {
              const Icon = topic.icon;

              return (
                <React.Fragment key={topic.id}>
                  <button
                    type="button"
                    className={
                      topic.progress === 100
                        ? "public_learning-journey-step learning-journey-step--complete"
                        : topic.progress > 0
                        ? "public_learning-journey-step learning-journey-step--active"
                        : "public_learning-journey-step"
                    }
                    onClick={() => handleTopicClick(topic)}
                  >
                    <div className="public_learning-journey-step__icon">
                      {topic.progress === 100 ? (
                        <CheckCircle aria-hidden="true" />
                      ) : (
                        <Icon aria-hidden="true" />
                      )}
                    </div>

                    <span>{topic.number}</span>
                    <strong>{topic.title}</strong>
                  </button>

                  {index < 4 && (
                    <div className="public_learning-journey__connector" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>

        {/* =====================================================
            TOPICS
        ===================================================== */}

        <section id="public_learning-topics" className="public_learning-topics">
          <div className="public_learning-section-heading fx fx-jb fx-ac wrap g2">
            <div>
              <div className="public_learning-section-label">
                LEARNING LIBRARY
              </div>

              <h2>Explore all learning paths</h2>

              <p>
                Practical guides designed around the way you
                actually use Universe Inventory.
              </p>
            </div>

            <button
              type="button"
              className={
                showFilters
                  ? "public_learning-filter-button learning-filter-button--active fx fx-ac g1"
                  : "public_learning-filter-button fx fx-ac g1"
              }
              onClick={() => setShowFilters((current) => !current)}
            >
              <Tune aria-hidden="true" />
              Filters
            </button>
          </div>

          {/* Filters */}

          {showFilters && (
            <div className="public_learning-filters fx fx-ac wrap g1">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={
                    activeCategory === category
                      ? "public_learning-filter-chip learning-filter-chip--active"
                      : "public_learning-filter-chip"
                  }
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {!showFilters && (
            <div className="public_learning-category-tabs fx wrap g1">
              {categories.map((category) => (
                <button
                  type="button"
                  key={category}
                  className={
                    activeCategory === category
                      ? "public_learning-category-tab learning-category-tab--active"
                      : "public_learning-category-tab"
                  }
                  onClick={() => setActiveCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          )}

          {filteredTopics.length > 0 ? (
            <div className="public_learning-grid">
              {filteredTopics.map((topic) => {
                const Icon = topic.icon;
                const isBookmarked = bookmarked.includes(topic.id);

                return (
                  <article
                    className={
                      topic.featured
                        ? "public_learning-card learning-card--featured"
                        : "public_learning-card"
                    }
                    key={topic.id}
                  >
                    <div className="public_learning-card__top fx fx-ac fx-jb">
                      <div
                        className="public_learning-card__icon"
                        aria-hidden="true"
                      >
                        <Icon />
                      </div>

                      <button
                        type="button"
                        className={
                          isBookmarked
                            ? "public_learning-bookmark learning-bookmark--active"
                            : "public_learning-bookmark"
                        }
                        onClick={() => toggleBookmark(topic.id)}
                        aria-label={
                          isBookmarked
                            ? `Remove ${topic.title} from bookmarks`
                            : `Bookmark ${topic.title}`
                        }
                      >
                        {isBookmarked ? (
                          <Bookmark aria-hidden="true" />
                        ) : (
                          <BookmarkBorder aria-hidden="true" />
                        )}
                      </button>
                    </div>

                    <div className="public_learning-card__number">
                      {topic.number}
                    </div>

                    <div className="public_learning-card__badges fx fx-ac wrap g1">
                      <span>{topic.category}</span>

                      {topic.featured && (
                        <span className="public_learning-card__featured-badge">
                          <Star aria-hidden="true" />
                          Recommended
                        </span>
                      )}
                    </div>

                    <h3>{topic.title}</h3>

                    <p>{topic.description}</p>

                    <div className="public_learning-card__details fx fx-ac wrap g2">
                      <span>
                        <MenuBook aria-hidden="true" />
                        {topic.lessons} lessons
                      </span>

                      <span>
                        <AccessTime aria-hidden="true" />
                        {topic.duration}
                      </span>
                    </div>

                    {topic.progress > 0 && (
                      <div className="public_learning-card__progress">
                        <div className="fx fx-jb">
                          <span>Progress</span>
                          <strong>{topic.progress}%</strong>
                        </div>

                        <div className="public_learning-progress__bar">
                          <span
                            style={{
                              width: `${topic.progress}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <button
                      type="button"
                      className={
                        topic.progress === 100
                          ? "public_learning-card__button learning-card__button--complete fx fx-ac fx-jc g1"
                          : "public_learning-card__button fx fx-ac fx-jc g1"
                      }
                      onClick={() => handleTopicClick(topic)}
                    >
                      {topic.progress === 100 ? (
                        <>
                          <CheckCircle aria-hidden="true" />
                          Completed
                        </>
                      ) : topic.progress > 0 ? (
                        <>
                          <PlayArrow aria-hidden="true" />
                          Continue
                        </>
                      ) : (
                        <>
                          Start Learning
                          <ArrowForward aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="public_learning-empty">
              <div className="public_learning-empty__icon">
                <Search aria-hidden="true" />
              </div>

              <h3>No learning resources found</h3>

              <p>
                We couldn't find a learning path matching your
                search. Try another keyword or clear your filters.
              </p>

              <button
                type="button"
                className="public_learning-primary-button"
                onClick={() => {
                  setSearch("");
                  setActiveCategory("All");
                }}
              >
                Clear Search
              </button>
            </div>
          )}
        </section>

        {/* =====================================================
            FEATURED MOBILE
        ===================================================== */}

        <section className="public_learning-mobile-feature">
          <div className="public_learning-mobile-feature__content">
            <div className="public_learning-section-label">
              LEARN ANYWHERE
            </div>

            <h2>
              Your business.
              <br />
              In your pocket.
            </h2>

            <p>
              Learn how to manage Universe Inventory from your
              mobile device, scan products, monitor stock, and
              stay connected to your business wherever you are.
            </p>

            <button
              type="button"
              className="public_learning-light-button fx fx-ac fx-jc g1"
              onClick={() =>
                handleTopicClick(
                  learningTopics.find(
                    (topic) => topic.id === "mobile-app"
                  )
                )
              }
            >
              Explore Mobile Learning
              <ArrowForward aria-hidden="true" />
            </button>
          </div>

          <div className="public_learning-mobile-feature__visual">
            <div className="public_learning-phone">
              <div className="public_learning-phone__speaker" />

              <div className="public_learning-phone__screen">
                <div className="public_learning-phone__header">
                  <span>Universe</span>
                  <NotificationsActive aria-hidden="true" />
                </div>

                <div className="public_learning-phone__welcome">
                  <small>Good morning</small>
                  <strong>Business Overview</strong>
                </div>

                <div className="public_learning-phone__stats">
                  <div>
                    <span>Sales</span>
                    <strong>₦245k</strong>
                  </div>

                  <div>
                    <span>Stock</span>
                    <strong>1,284</strong>
                  </div>
                </div>

                <div className="public_learning-phone__chart">
                  <div style={{ height: "42%" }} />
                  <div style={{ height: "68%" }} />
                  <div style={{ height: "52%" }} />
                  <div style={{ height: "82%" }} />
                  <div style={{ height: "72%" }} />
                  <div style={{ height: "92%" }} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =====================================================
            QUICK HELP
        ===================================================== */}

        <section className="public_learning-help">
          <div className="public_learning-help__icon">
            <MenuBook aria-hidden="true" />
          </div>

          <div className="fg1">
            <div className="public_learning-section-label">
              NEED MORE HELP?
            </div>

            <h2>Can't find what you're looking for?</h2>

            <p>
              Explore the full documentation or contact our support
              team for assistance with your Universe Inventory
              account.
            </p>
          </div>

          <div className="public_learning-help__actions fx fx-ac wrap g2">
            <button
              type="button"
              className="public_learning-secondary-button"
              onClick={() => {
                window.location.href = "/documentation";
              }}
            >
              Documentation
            </button>

            <button
              type="button"
              className="public_learning-primary-button"
              onClick={() => {
                window.location.href = "/support";
              }}
            >
              Contact Support
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}

export default PublicLearning;